import { useRouter } from 'next/router'
import qs from 'qs'
import { useEffect, useMemo, useState } from 'react'
import { useAccountMeQuery } from '~/api/accounts'
import { useGetReportsQuery } from '~/api/reports'
import { IS_MERGED_REPORTS_UI_ENABLED } from '~/common/feature_flags'
import { Msg, useMsg } from '~/common/localizationUtils'
import { PageErrorState, PageNoResultsState, SadStates } from '~/components/SadStates'
import { BulkActionsBanner } from '~/components/v2/BulkActionsBanner'
import { ItemsList, ItemsListItem } from '~/components/v2/ItemsList'
import { ActionBar, ResultsLayout } from '~/components/v2/ResultsLayout'
import { useFilterParams } from '~/context/filters'
import { reportOrdering, reportTypeFilter } from '~/custom_reports/constants'
import { ReportItem } from '~/custom_reports/home/ReportItem'
import { MergedReportItem } from '~/custom_reports/home/MergedReportItem'
import { MergeReportsModal } from '~/custom_reports/home/MergeReportsModal'
import { maxSelectableReports, useBulkExport } from '~/custom_reports/hooks/useBulkExport'
import {
  Box,
  Button,
  Card,
  CardBody,
  FilterTab,
  Flex,
  Link as ChakraLink,
  Pagination,
  PaginationControls,
  PaginationNextButton,
  PaginationPageNumbers,
  PaginationPreviousButton,
  PaginationTotalItems,
  Skeleton,
  Text,
  Tooltip,
  useDisclosure
} from '~/design_system'
import { useStringUrlState, useSyncToUrlState } from '~/hooks/useUrlState'
import { VsyncReportExportButton } from '~/reports/table/VsyncReportTransfer'
import { useFeatureFlag } from '~/hooks/useFeatureFlag'
import { useReportPermissions } from '../api'

export const Attribute = ({
  icon,
  text,
  tooltip,
  url = '',
  'data-testid': dataTestId = 'attribute-data-testid',
  ...rest
}) => {
  const Attribute = (
    <>
      {text && (
        <Tooltip label={tooltip} placement="bottom" hasArrow showDelay={500}>
          <Flex alignItems="center" mr={4} {...rest}>
            <Box as={icon} mr={1} color="var(--vndly-color-container-icon)" w="16px" />
            <Text textStyle="bodySmall" m={0} data-testid={dataTestId}>
              {text}
            </Text>
          </Flex>
        </Tooltip>
      )}
    </>
  )

  if (url && typeof url === 'string' && url.length > 0) {
    return (
      <ChakraLink variant="unstyled" href={url} textDecoration="underline">
        {Attribute}
      </ChakraLink>
    )
  }

  return Attribute
}

export function ReportsSkeleton() {
  return (
    <ItemsList>
      {[...Array(10).keys()].map(value => (
        <ItemsListItem key={value} p={4} display="block">
          <Skeleton width="full" height={16} />
        </ItemsListItem>
      ))}
    </ItemsList>
  )
}

/**
 * Reads either the current `view=scheduled|favorites` URL param, or the legacy `scheduled`/`favorites`
 * presence-keyed params (from links bookmarked/shared before the mini tab was written as `view=`), so
 * both keep working.
 *
 * @param queryParams
 * @returns {'scheduled'|'favorites'|'all'}
 */
export const getSelectedMiniTabValue = queryParams => {
  if (queryParams.view === 'scheduled' || queryParams.view === 'favorites') return queryParams.view
  const selectedTab = Object.keys(queryParams).find(
    queryParam => queryParam === 'scheduled' || queryParam === 'favorites'
  )
  return selectedTab || 'all'
}

export const transformFilterParams = filterParams => {
  const filters = { ...filterParams }

  // filterParams comes from useSyncToUrlState, which reads the entire shared URL query state (not just
  // the sidebar filter fields) -- exclude `view` (the selected mini tab's own URL key) so it doesn't leak
  // into the reports API request alongside the `[selectedMiniTab]: true` param that already represents it.
  delete filters.view

  if (filters.report_type) {
    filters.report_type = filters.report_type
      .split(',')
      .map(x => reportTypeFilter[x])
      .join(',')
  }
  return filters
}

const LIMIT_PER_PAGE = 10
export default function ReportsPanel({ onFiltersToggle, unionMergeOnly = false }) {
  const msg = useMsg()
  const router = useRouter()
  const query = router.query
  const { data: isMergedReportsUiEnabled } = useFeatureFlag(IS_MERGED_REPORTS_UI_ENABLED)
  const { data: me } = useAccountMeQuery()
  const { permissions, isLoading: isPermissionsLoading } = useReportPermissions()
  const filterParams = useSyncToUrlState(useFilterParams())

  const [ordering, setOrdering] = useStringUrlState('ordering', reportOrdering.newest)
  const [searchTerm, setSearchTerm] = useStringUrlState('search')
  const [offset, setOffset] = useState(query.offset)
  const [selectedMiniTab, setSelectedMiniTab] = useStringUrlState('view', getSelectedMiniTabValue(query))

  const {
    isLoading: isReportsLoading,
    data: { results, total_pages, page, count } = {},
    status
  } = useGetReportsQuery({
    limit: LIMIT_PER_PAGE,
    offset,
    ordering,
    search: searchTerm,
    ...transformFilterParams(filterParams),
    ...(unionMergeOnly && { union_merge: true }),
    [selectedMiniTab]: true
  })
  const canExportReportDefinitions = useMemo(
    () => Boolean(permissions && (permissions.canVsyncReport || permissions.canExportReportDefinitions)),
    [permissions]
  )
  // The backend's perform_destroy has no owner check, so deletion is gated here the same way the
  // report builder gates it: full authors may delete their own reports, superusers may delete any.
  // Merged reports use the merge-create permission instead of the base author permission.
  const canDeleteReport = report =>
    Boolean(
      (unionMergeOnly ? permissions?.canCreateMergeReport : permissions?.canAuthorReport?.full) &&
        (report.created_by === me?.id || permissions?.isReportSuperuser)
    )

  const isLoading = isReportsLoading || isPermissionsLoading

  const {
    selectedReports,
    deselectAllReports,
    deselectReport,
    onBulkExport,
    isBulkExporting,
    onSelectReport,
    isReportSelectable,
    isReportSelected
  } = useBulkExport(canExportReportDefinitions)

  const mergeModal = useDisclosure()

  // reset the paging when filters are changed
  useEffect(() => {
    /*
      eslint-disable-next-line react-hooks/set-state-in-effect --

      ⚠️ HEY YOU, DEV! Yes, YOU! Don't you look away...I know you are reading this. This is your problem now.
      Fix the below while you are working in here. It's simple, just delete this comment, and you'll see the lint error.
      There's even a suggestion on how you might fix it. These particular lint errors are serious issues that we
      couldn't feasibly fix across the whole codebase, but they need fixed. Your help is appreciated! 🥳

      Issue: Resetting offset when filters change causes extra render.
      Possible fix approach: Combine offset reset with filter state updates using useReducer.
    */
    setOffset(undefined)
  }, [searchTerm, filterParams, selectedMiniTab])

  // when the back button is clicked, we need to update the reports list based on query params
  useEffect(() => {
    if (router.isReady) {
      router.beforePopState(state => {
        const queryParams = qs.parse(state.url.split('?')[1], { ignoreQueryPrefix: true })
        setOrdering(queryParams.ordering ?? reportOrdering.newest)
        setSearchTerm(queryParams.search)
        setOffset(queryParams.offset)
        setSelectedMiniTab(getSelectedMiniTabValue(queryParams))
        return true
      })
    }
  }, [router.isReady])

  return (
    <ResultsLayout.Main>
      <ActionBar>
        <ActionBar.Left>
          {!unionMergeOnly && <ActionBar.ToggleFiltersButton onClick={onFiltersToggle} />}
          <ActionBar.Search value={searchTerm} onChange={setSearchTerm} />
          <ActionBar.ResultCount count={count} isLoading={isLoading} />
        </ActionBar.Left>
        <ActionBar.FilterTabs value={selectedMiniTab} onChange={setSelectedMiniTab}>
          <FilterTab value="all" data-testid="mini-tab-all">
            <Msg id="common.ui.all" />
          </FilterTab>
          {!unionMergeOnly && (
            <FilterTab value="scheduled" data-testid="report-home-mini-tab-scheduled-btn">
              <Msg id="common.ui.scheduled" />
            </FilterTab>
          )}
          <FilterTab value="favorites" data-testid="report-home-mini-tab-favorites-btn">
            <Msg id="common.ui.favorites" />
          </FilterTab>
        </ActionBar.FilterTabs>
        <ActionBar.Right>
          <ActionBar.SortBy value={ordering} onChange={setOrdering}>
            <option value={reportOrdering.newest}>{msg('common.ui.sort.most_recent_first')}</option>
            <option value="oldest">{msg('common.ui.sort.oldest_first')}</option>
            <option value="lastRan">{msg('custom_report.home.sort.last_run')}</option>
            <option value={reportOrdering.nameAZ}>{msg('common.ui.name_a_z')}</option>
            <option value="nameZA">{msg('common.ui.name_z_a')}</option>
          </ActionBar.SortBy>
        </ActionBar.Right>
      </ActionBar>
      <ResultsLayout.MainContent>
        <SadStates
          states={[
            {
              when: isLoading || !results,
              render: ReportsSkeleton
            },
            {
              when: status === 'error',
              render: () => (
                <Card>
                  <CardBody>
                    <PageErrorState message="There was an error in loading reports. Please try again later." />
                  </CardBody>
                </Card>
              )
            }
          ]}>
          <>
            {selectedReports.length > 0 && (
              <BulkActionsBanner
                entityNameSingular="Report"
                entityNamePlural="Reports"
                selectedItemsMaxCount={maxSelectableReports}
                selectedItemsCount={selectedReports.length}
                mb={2}
                data-testid="reportsBulkBanner">
                {isMergedReportsUiEnabled &&
                  permissions?.canCreateMergeReport &&
                  !unionMergeOnly &&
                  selectedReports.length > 1 && (
                    <Button
                      data-testid="merge-selected-reports-button"
                      key="MergeReportsButton"
                      variant="secondary"
                      onClick={mergeModal.onOpen}>
                      <Msg id="custom_report.merge.button" defaultMessage="Merge Reports" />
                    </Button>
                  )}
                <Button
                  data-testid="bulk-export-selected-report-button"
                  key="BulkExportButton"
                  display={selectedReports.length > 4 && canExportReportDefinitions ? 'none' : null}
                  onClick={onBulkExport}
                  isDisabled={selectedReports.length > 4 && canExportReportDefinitions}
                  isLoading={isBulkExporting}
                  loadingText="Exporting">
                  Export
                </Button>
                {canExportReportDefinitions && !unionMergeOnly && (
                  <VsyncReportExportButton
                    key="export-report-via-vsync-button"
                    reports={selectedReports.filter(r => r.report_type !== 'standard')}
                    buttonStyle="button"
                    variant="secondary"
                  />
                )}
                <Button
                  data-testid="cancel-bulk-action-selected-report-button"
                  key="BulkExportCancelButton"
                  variant="secondary"
                  onClick={deselectAllReports}
                  isDisabled={isBulkExporting}>
                  Cancel
                </Button>
              </BulkActionsBanner>
            )}
            <ItemsList aria-label="Reports List">
              {results?.map((report, index) =>
                unionMergeOnly ? (
                  <MergedReportItem
                    report={report}
                    key={report?.id}
                    onSelectReport={event => onSelectReport(report, event)}
                    canExport={permissions?.canExportReport ?? false}
                    canDelete={canDeleteReport(report)}
                    isSelectable={isReportSelectable(report)}
                    isSelected={isReportSelected(report)}
                    index={index}
                  />
                ) : (
                  <ReportItem
                    report={report}
                    key={report?.id}
                    onSelectReport={event => onSelectReport(report, event)}
                    canExport={permissions?.canExportReport ?? false}
                    isSelectable={isReportSelectable(report)}
                    isSelected={isReportSelected(report)}
                    index={index}
                  />
                )
              )}
              {results?.length === 0 && (
                <ItemsList>
                  <PageNoResultsState
                    heading="No Results"
                    content="Check your spelling and/or adjust filters to try again"
                  />
                </ItemsList>
              )}
            </ItemsList>
          </>
        </SadStates>
      </ResultsLayout.MainContent>
      {results?.length && (
        <Pagination
          aria-label="Pagination"
          totalPages={total_pages}
          totalCount={count}
          resultCount={LIMIT_PER_PAGE}
          currentPage={page}
          onPageChange={number => {
            setOffset((number - 1) * LIMIT_PER_PAGE)
          }}>
          <PaginationControls>
            <PaginationPreviousButton />
            <PaginationPageNumbers />
            <PaginationNextButton />
          </PaginationControls>
          <PaginationTotalItems />
        </Pagination>
      )}
      {mergeModal.isOpen && (
        <MergeReportsModal
          isOpen={mergeModal.isOpen}
          onClose={mergeModal.onClose}
          selectedReports={selectedReports}
          onDeselectReport={deselectReport}
        />
      )}
    </ResultsLayout.Main>
  )
}
